// Settings → General: the admin's discount and service-charge controls
// (F-CTRL-N80). All off until an admin turns them on; every manual discount
// and service-charge change is logged either way. The server stores only a
// validated object and refuses a non-admin write, so the card is admin-only.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { DISCOUNT_CONTROLS_KEY, parseDiscountControls, type DiscountControls } from '@/lib/discountControls'
import { getStoredUserRole } from '@/lib/staff-roles'

export function DiscountControlsSettingCard() {
  const all = useAllSettings()
  const stored = parseDiscountControls(all?.[DISCOUNT_CONTROLS_KEY])
  const storedKey = JSON.stringify(stored)
  const qc = useQueryClient()
  const { toast } = useToast()
  const [reasons, setReasons] = useState(stored.reasons_required)
  const [limit, setLimit] = useState(stored.pin_above_percent == null ? '' : String(stored.pin_above_percent))
  const [waivePin, setWaivePin] = useState(stored.pin_to_waive_service_charge)
  useEffect(() => {
    setReasons(stored.reasons_required)
    setLimit(stored.pin_above_percent == null ? '' : String(stored.pin_above_percent))
    setWaivePin(stored.pin_to_waive_service_charge)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey])

  const limitNum = limit.trim() === '' ? null : Number(limit)
  const limitValid = limitNum == null || (Number.isFinite(limitNum) && limitNum >= 1 && limitNum <= 100)
  const next: DiscountControls = { reasons_required: reasons, pin_above_percent: limitValid ? limitNum : null, pin_to_waive_service_charge: waivePin }
  const dirty = JSON.stringify(next) !== storedKey

  const save = useMutation({
    mutationFn: async (value: DiscountControls) => {
      const res = await apiClient.updateSetting(DISCOUNT_CONTROLS_KEY, value)
      if (!res.success) throw new Error(res.message || 'Could not save')
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      toast({ title: 'Settings saved', description: 'Counters pick this up after they reload.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  if (getStoredUserRole() !== 'admin') return null

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Discount and service-charge controls</CardTitle>
        <CardDescription>
          Every manual discount and service-charge change is recorded with who made it. These add a reason and a manager PIN
          at the counter.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-3">
          <Switch id="discount-reasons" checked={reasons} onCheckedChange={setReasons} />
          <div className="space-y-0.5">
            <Label htmlFor="discount-reasons" className="text-base">
              Ask for a reason
            </Label>
            <p className="text-sm text-muted-foreground">
              One tap when a cashier gives a discount or waives the service charge: regular customer, owner&rsquo;s guest,
              complaint, staff, promotion or other.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="discount-pin-limit" className="text-base">
            Need a manager PIN for a discount over
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="discount-pin-limit"
              inputMode="decimal"
              className="h-11 w-24"
              placeholder="Off"
              value={limit}
              onChange={e => setLimit(e.target.value.replace(/[^\d.]/g, ''))}
              aria-invalid={!limitValid}
            />
            <span className="text-sm text-muted-foreground">% of the bill</span>
          </div>
          <p className={limitValid ? 'text-sm text-muted-foreground' : 'text-sm text-danger-fg'}>
            {limitValid
              ? 'Counts the bill and item discounts together. Leave blank for no limit.'
              : 'Enter a number from 1 to 100, or leave it blank.'}
          </p>
        </div>

        <div className="flex items-start gap-3">
          <Switch id="discount-waive-pin" checked={waivePin} onCheckedChange={setWaivePin} />
          <div className="space-y-0.5">
            <Label htmlFor="discount-waive-pin" className="text-base">
              Need a manager PIN to waive the service charge
            </Label>
            <p className="text-sm text-muted-foreground">Lowering it to another rate never asks.</p>
          </div>
        </div>

        <Button className="h-11 px-6" disabled={save.isPending || !dirty || !limitValid} onClick={() => save.mutate(next)}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
